import React, { useEffect, useState } from "react";
import { useParams, useHistory } from "react-router-dom";
import { Button, Card, Tag, Modal, Form, Select, Space, message, Row, Col, Divider, Spin, Avatar, Breadcrumb } from "antd";
import { ArrowLeftOutlined, EyeOutlined, CheckOutlined, CloseOutlined, FilePdfOutlined, IdcardOutlined, BookOutlined, UserOutlined } from "@ant-design/icons";
import { getReq, postReq } from "../api";
import useUserDetails from "../utils/useUserDetails";
import Swal from "sweetalert2";
import { toastNotification } from "../utils/toasthelper";

const { Option } = Select;

const OnboardingCandidateDetails = () => {
  const { id } = useParams();
  const history = useHistory();
  const { userDetails } = useUserDetails();
  const companyId = userDetails?.companyId || userDetails?._id;

  const [currentApp, setCurrentApp] = useState(null);
  const [roles, setRoles] = useState([]);
  const [usersList, setUsersList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);

  // Document active state
  const [activeDocKey, setActiveDocKey] = useState("resume");
  const [selectVisible, setSelectVisible] = useState(false);
  const [form] = Form.useForm();

  const fetchApplicationDetails = async () => {
    setLoading(true);
    try {
      const res = await postReq(`/user/onboarding/get/${id}`);
      if (res && res.status === 1) {
        setCurrentApp(res.data);
      } else {
        message.error(res.message || "Failed to load candidate details");
        history.push("/company/onboarding");
      }
    } catch (err) {
      console.error(err);
      message.error("Error loading onboarding details");
      history.push("/company/onboarding");
    } finally {
      setLoading(false);
    }
  };

  const fetchMetadata = async () => {
    try {
      // Fetch Roles
      const rolesRes = await getReq("/user/roles");
      if (rolesRes && rolesRes.status === 1) {
        setRoles(rolesRes.data || []);
      } else if (Array.isArray(rolesRes)) {
        setRoles(rolesRes);
      } else if (rolesRes?.data && Array.isArray(rolesRes.data)) {
        setRoles(rolesRes.data);
      }

      // Fetch Users for Reporting Manager selection
      const usersRes = await getReq("/user/dropdown_user");
      if (usersRes && usersRes.status === 200) {
        setUsersList(usersRes.data || []);
      }
    } catch (err) {
      console.error("Error fetching metadata:", err);
    }
  };

  useEffect(() => {
    fetchApplicationDetails();
    fetchMetadata();
  }, [id]);

  const handleReject = async () => {
    if (!currentApp) return;
    try {
      const result = await Swal.fire({
        title: "Reject Candidate?",
        text: `Are you sure you want to reject ${currentApp.name}'s onboarding request?`,
        icon: "warning",
        showCancelButton: true,
        confirmButtonColor: "#d33",
        cancelButtonColor: "#3085d6",
        confirmButtonText: "Yes, reject",
      });

      if (result.isConfirmed) {
        const res = await postReq(`/user/onboarding/update-status/${currentApp._id}`, {
          status: "REJECTED",
        });
        if (res && res.status === 1) {
          toastNotification("success", "Candidate request rejected");
          fetchApplicationDetails();
        } else {
          toastNotification("error", res.message || "Failed to update status");
        }
      }
    } catch (err) {
      console.error(err);
      toastNotification("error", "An error occurred");
    }
  };

  const handleSelectSubmit = async (values) => {
    try {
      setSubmitting(true);
      const res = await postReq(`/user/onboarding/select/${currentApp._id}`, {
        roleId: values.roleId,
        reportingManager: values.reportingManager,
      });

      if (res && res.status === 1) {
        toastNotification("success", "Candidate selected and user created successfully!");
        setSelectVisible(false);
        fetchApplicationDetails();
      } else {
        toastNotification("error", res.message || "Failed to onboard user");
      }
    } catch (err) {
      console.error(err);
      toastNotification("error", "Error creating user account");
    } finally {
      setSubmitting(false);
    }
  };

  const getFileUrl = (filename) => {
    if (!filename) return "#";
    const apiBase = process.env.REACT_APP_API_URL || "http://localhost:9003/api";
    if (apiBase.includes("localhost")) {
      return `${apiBase.replace("/api", "")}files/${filename}`;
    }
    return `${apiBase}/files/${filename}`;
  };

  const isImageFile = (filename) => {
    if (!filename) return false;
    const ext = filename.split(".").pop().toLowerCase();
    return ["jpg", "jpeg", "png", "gif", "webp"].includes(ext);
  };

  const downloadAllDocs = () => {
    if (!currentApp) return;
    const docs = [
      currentApp.resume,
      currentApp.aadharCard,
      currentApp.degreeCertificate,
      currentApp.document1,
      currentApp.document2,
    ].filter(Boolean);

    docs.forEach((filename, index) => {
      setTimeout(() => {
        const link = document.createElement("a");
        link.href = getFileUrl(filename);
        link.setAttribute("download", filename);
        link.setAttribute("target", "_blank");
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
      }, index * 300);
    });
  };

  if (loading) {
    return (
      <div style={{ display: "flex", justifyContent: "center", alignItems: "center", height: "80vh" }}>
        <Spin size="large" tip="Loading candidate details..." />
      </div>
    );
  }

  if (!currentApp) return null;

  const docTabs = [
    { key: "resume", label: "Resume", filename: currentApp.resume },
    { key: "aadhar", label: "Aadhar Card", filename: currentApp.aadharCard },
    { key: "degree", label: "Degree Certificate", filename: currentApp.degreeCertificate },
    currentApp.document1 ? { key: "doc1", label: "Document 1", filename: currentApp.document1 } : null,
    currentApp.document2 ? { key: "doc2", label: "Document 2", filename: currentApp.document2 } : null,
  ].filter(Boolean);

  const selectedDoc = docTabs.find((tab) => tab.key === activeDocKey) || docTabs[0];

  return (
    <div style={{ padding: "4px" }}>
      {/* Breadcrumb section */}
      <Breadcrumb style={{ marginBottom: 16 }}>
        <Breadcrumb.Item href="/company/onboarding">Onboarding</Breadcrumb.Item>
        <Breadcrumb.Item>User Info</Breadcrumb.Item>
      </Breadcrumb>

      <Card
        bordered={false}
        style={{ borderRadius: 12, boxShadow: "0 4px 20px rgba(0, 0, 0, 0.05)", overflow: "hidden" }}
        bodyStyle={{ padding: "30px" }}
      >
        {/* Top Header Section */}
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 30, borderBottom: "1px solid #f0f0f0", paddingBottom: 24 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 20 }}>
            <Avatar size={80} icon={<UserOutlined />} style={{ backgroundColor: "#13487D" }} />
            <div>
              <h2 style={{ margin: 0, fontSize: 24, fontWeight: 700, color: "#13487D" }}>{currentApp.name}</h2>
              <div style={{ color: "#666", fontSize: 15, marginTop: 2 }}>Onboarding Candidate</div>
              <div style={{ color: "#888", fontSize: 13, marginTop: 4 }}>Applied On {new Date(currentApp.createdAt).toLocaleDateString()}</div>
            </div>
          </div>
          <div style={{ display: "flex", alignItems: "center", gap: 32 }}>
            <div>
              <div style={{ color: "#888", fontSize: 12, fontWeight: 500, marginBottom: 4 }}>STATUS</div>
              <Tag color={currentApp.status === "SELECTED" ? "green" : currentApp.status === "REJECTED" ? "red" : "orange"} style={{ fontWeight: 600, padding: "4px 12px", borderRadius: 4, fontSize: 13 }}>
                {currentApp.status}
              </Tag>
            </div>
            {/* <div>
              <div style={{ color: "#888", fontSize: 12, fontWeight: 500, marginBottom: 4 }}>QUALIFICATION</div>
              <div style={{ fontWeight: 600, fontSize: 16, color: "#333" }}>{currentApp.qualification || "N/A"}</div>
            </div> */}
            {currentApp.status === "PENDING" && (
              <div style={{ paddingLeft: 10 }}>
                <Space>
                  <Button
                    type="primary"
                    icon={<CheckOutlined />}
                    style={{ backgroundColor: "#52c41a", borderColor: "#52c41a", borderRadius: 6, fontWeight: 600 }}
                    onClick={() => setSelectVisible(true)}
                  >
                    Approve & Onboard
                  </Button>
                  <Button type="primary" danger icon={<CloseOutlined />} style={{ borderRadius: 6 }} onClick={handleReject}>
                    Reject
                  </Button>
                </Space>
              </div>
            )}
          </div>
        </div>

        {/* Split Grid Body */}
        <Row gutter={32}>
          {/* Left Panel - Personal & Job Details */}
          <Col xs={24} md={8} style={{ borderRight: "1px solid #f0f0f0", paddingRight: 32 }}>
            <div style={{ marginBottom: 24 }}>
              <div style={{ fontWeight: 600, color: "#444", fontSize: 13, textTransform: "uppercase", letterSpacing: "0.5px", marginBottom: 6 }}>Email</div>
              <div style={{ color: "#1890ff", wordBreak: "break-all", fontSize: 15, fontWeight: 500 }}>{currentApp.email}</div>
            </div>
            <div style={{ marginBottom: 24 }}>
              <div style={{ fontWeight: 600, color: "#444", fontSize: 13, textTransform: "uppercase", letterSpacing: "0.5px", marginBottom: 6 }}>Phone No</div>
              <div style={{ color: "#333", fontSize: 15, fontWeight: 500 }}>{currentApp.mobileNo}</div>
            </div>
            <div style={{ marginBottom: 24 }}>
              <div style={{ fontWeight: 600, color: "#444", fontSize: 13, textTransform: "uppercase", letterSpacing: "0.5px", marginBottom: 6 }}>Current Address</div>
              <div style={{ color: "#555", fontSize: 15, lineHeight: 1.5 }}>{currentApp.currentAddress}</div>
            </div>
            <div style={{ marginBottom: 24 }}>
              <div style={{ fontWeight: 600, color: "#444", fontSize: 13, textTransform: "uppercase", letterSpacing: "0.5px", marginBottom: 6 }}>Permanent Address</div>
              <div style={{ color: "#555", fontSize: 15, lineHeight: 1.5 }}>{currentApp.permanentAddress}</div>
            </div>
            <div style={{ marginBottom: 24 }}>
              <div style={{ fontWeight: 600, color: "#444", fontSize: 13, textTransform: "uppercase", letterSpacing: "0.5px", marginBottom: 6 }}>Previous Company</div>
              <div style={{ color: "#555", fontSize: 15, fontWeight: 500 }}>{currentApp.previousCompany || "N/A"}</div>
            </div>
            <div style={{ marginBottom: 24 }}>
              <div style={{ fontWeight: 600, color: "#444", fontSize: 13, textTransform: "uppercase", letterSpacing: "0.5px", marginBottom: 6 }}>Previous Salary</div>
              <div style={{ color: "#555", fontSize: 15, fontWeight: 500 }}>{currentApp.previousSalary || "N/A"}</div>
            </div>
            <div style={{ marginBottom: 24 }}>
              <div style={{ fontWeight: 600, color: "#444", fontSize: 13, textTransform: "uppercase", letterSpacing: "0.5px", marginBottom: 6 }}>Qualification</div>
              <div style={{ color: "#555", fontSize: 15, fontWeight: 500 }}>{currentApp.qualification || "N/A"}</div>
            </div>
          </Col>

          {/* Right Panel - Tab & Document Viewer */}
          <Col xs={24} md={16} style={{ paddingLeft: 32 }}>
            {/* Fake Tabs headers matching the screenshot */}
            <div style={{ borderBottom: "2px solid #f0f0f0", marginBottom: 24, display: "flex", gap: 32, fontSize: 15 }}>
              <div style={{ borderBottom: "2px solid #1890ff", paddingBottom: 12, color: "#1890ff", fontWeight: 600, cursor: "pointer" }}>
                Documents
              </div>
              <div style={{ paddingBottom: 12, color: "#ccc", cursor: "not-allowed" }}>Attendance</div>
              <div style={{ paddingBottom: 12, color: "#ccc", cursor: "not-allowed" }}>Module Access</div>
              <div style={{ paddingBottom: 12, color: "#ccc", cursor: "not-allowed" }}>Salary Records</div>
            </div>

            {/* Document Pill selection */}
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 20 }}>
              <Space wrap size={10}>
                {docTabs.map((tab) => (
                  <Button
                    key={tab.key}
                    onClick={() => setActiveDocKey(tab.key)}
                    style={{
                      borderRadius: 6,
                      backgroundColor: activeDocKey === tab.key ? "#e6f7ff" : "#f5f5f5",
                      color: activeDocKey === tab.key ? "#1890ff" : "#555",
                      borderColor: activeDocKey === tab.key ? "#bae7ff" : "#d9d9d9",
                      fontWeight: activeDocKey === tab.key ? 600 : 400,
                      // height: 36,
                    }}
                  >
                    {tab.label}
                  </Button>
                ))}
              </Space>
              <Button type="link" onClick={downloadAllDocs} style={{ color: "#1890ff", fontWeight: 600, fontSize: 14 }}>
                Download All
              </Button>
            </div>

            {/* Selection info bar */}
            {selectedDoc && (
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", background: "#f9fbfd", border: "1px solid #e6f0fa", borderRadius: 6, padding: "12px 18px", marginBottom: 20 }}>
                <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                  <FilePdfOutlined style={{ fontSize: 20, color: "#ff4d4f" }} />
                  <span style={{ fontWeight: 500, color: "#333", fontSize: 14 }}>{selectedDoc.filename}</span>
                </div>
                <a href={getFileUrl(selectedDoc.filename)} target="_blank" rel="noreferrer" style={{ color: "#1890ff", fontWeight: 600, display: "flex", alignItems: "center", gap: 6 }}>
                  Open <EyeOutlined />
                </a>
              </div>
            )}

            {/* Embed Sandbox Preview */}
            {selectedDoc && (
              <div style={{ border: "1px solid #e8e8e8", borderRadius: 8, overflow: "hidden", background: "#fafafa", boxShadow: "0 2px 8px rgba(0,0,0,0.02)" }}>
                {isImageFile(selectedDoc.filename) ? (
                  <div style={{ padding: 30, textAlign: "center" }}>
                    <img
                      src={getFileUrl(selectedDoc.filename)}
                      alt={selectedDoc.label}
                      style={{ maxWidth: "100%", maxHeight: "500px", objectFit: "contain", borderRadius: 4 }}
                    />
                  </div>
                ) : (
                  <iframe
                    src={getFileUrl(selectedDoc.filename)}
                    title={selectedDoc.label}
                    width="100%"
                    height="550px"
                    style={{ border: "none" }}
                  />
                )}
              </div>
            )}
          </Col>
        </Row>
      </Card>

      {/* Select & Role Assignment Modal */}
      <Modal
        title={
          <span style={{ fontSize: 18, fontWeight: 700, color: "#52c41a" }}>
            Approve & Onboard User
          </span>
        }
        open={selectVisible}
        onCancel={() => setSelectVisible(false)}
        footer={null}
        width={500}
      >
        {currentApp && (
          <Form form={form} layout="vertical" onFinish={handleSelectSubmit} style={{ marginTop: 15 }}>
            <p style={{ color: "#666", marginBottom: 20 }}>
              Assign a role and tech skills for <strong>{currentApp.name}</strong> to finalize their onboarding and create their login account.
            </p>

            <Form.Item
              name="roleId"
              label="Assign Role"
              rules={[{ required: true, message: "Please assign a system role" }]}
            >
              <Select placeholder="Select role" size="large">
                {roles.map((role) => (
                  <Option key={role._id} value={role._id}>
                    {role.name}
                  </Option>
                ))}
              </Select>
            </Form.Item>

            <Form.Item
              name="reportingManager"
              label="Select Reporting Manager"
            >
              <Select placeholder="Select reporting manager" size="large" allowClear>
                {usersList.map((usr) => (
                  <Option key={usr._id} value={usr._id}>
                    {usr.name}
                  </Option>
                ))}
              </Select>
            </Form.Item>


            <Form.Item style={{ marginTop: 30, textAlign: "right" }}>
              <Space>
                <Button onClick={() => setSelectVisible(false)} style={{ borderRadius: 6 }}>
                  Cancel
                </Button>
                <Button type="primary" htmlType="submit" loading={submitting} style={{ backgroundColor: "#52c41a", borderColor: "#52c41a", borderRadius: 6 }}>
                  Submit & Onboard User
                </Button>
              </Space>
            </Form.Item>
          </Form>
        )}
      </Modal>
    </div>
  );
};

export default OnboardingCandidateDetails;
